/**
 * Smart Onboarding Service
 *
 * POSTs the full HOA onboarding wizard payload to the droplet-hosted
 * landing-pages-api at /api/v1/hoa/onboarding-submission.
 *
 * The droplet route validates required fields in snake_case form
 * (community_name, total_units, community_type, contact_name, contact_email)
 * with no fallback for camelCase. OnboardingFormData uses camelCase keys, so
 * we transform the payload to snake_case at the boundary before posting.
 *
 * The route also handles the stakeholders array via a separate
 * /api/v1/hoa/stakeholder-contact endpoint keyed by submission_id; we fan
 * that out after the parent insert succeeds.
 */

import type { OnboardingFormData, OnboardingSubmissionResponse } from '@/types/onboarding';

const API_BASE_URL = import.meta.env.VITE_LANDING_PAGES_API_URL || 'http://localhost:3002';

function camelToSnake(key: string): string {
  return key.replace(/[A-Z]/g, (m) => '_' + m.toLowerCase());
}

function toSnakeCaseKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(toSnakeCaseKeys);
  }
  if (value !== null && typeof value === 'object' && value.constructor === Object) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[camelToSnake(k)] = toSnakeCaseKeys(v);
    }
    return out;
  }
  return value;
}

interface OnboardingApiResponse {
  success: boolean;
  message?: string;
  error?: string;
  data?: {
    id: string;
    submission_id?: string;
    community_name?: string;
    created_at?: string;
  };
}

interface StakeholderApiResponse {
  success: boolean;
  message?: string;
  error?: string;
  data?: { id: string };
}

class SmartOnboardingService {
  async submitOnboarding(
    formData: OnboardingFormData,
    _bypassValidation: boolean = false,
  ): Promise<OnboardingSubmissionResponse | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/hoa/onboarding-submission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toSnakeCaseKeys(formData)),
      });

      if (response.status === 429) {
        return {
          success: false,
          submissionId: '',
          message:
            'Too many submissions from this network. Please wait a few minutes and try again.',
        };
      }

      const result: OnboardingApiResponse = await response.json().catch(() => ({
        success: false,
        error: 'Invalid response from server',
      }));

      if (!response.ok || !result.success || !result.data?.id) {
        return {
          success: false,
          submissionId: '',
          message:
            result.message || result.error || 'Failed to submit onboarding form. Please try again.',
        };
      }

      const submissionId = result.data.id;

      // Fan out stakeholders to /api/v1/hoa/stakeholder-contact (does not block
      // the main submission's success - log failures but still return success).
      if (formData.keyStakeholders && formData.keyStakeholders.length > 0) {
        await Promise.all(
          formData.keyStakeholders.map((stakeholder) =>
            this.submitStakeholder(submissionId, stakeholder),
          ),
        );
      }

      return {
        success: true,
        submissionId,
        message: result.message || 'Onboarding submission received',
        nextSteps: {
          calendarLink: 'https://calendly.com/community-app/discovery-call',
          followUpEmail: 'Our team will follow up within 2 business hours.',
          estimatedResponseTime: '2 business hours',
        },
      };
    } catch (error) {
      console.error('[smartOnboardingService] submission failed:', error);
      return {
        success: false,
        submissionId: '',
        message:
          'Network error submitting onboarding form. Please check your connection and try again.',
      };
    }
  }

  private async submitStakeholder(
    submissionId: string,
    stakeholder: { title: string; name: string; email: string },
  ): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/hoa/stakeholder-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submission_id: submissionId,
          name: stakeholder.name,
          title: stakeholder.title,
          email: stakeholder.email,
        }),
      });

      if (!response.ok) {
        const result: StakeholderApiResponse = await response
          .json()
          .catch(() => ({ success: false, error: 'Invalid response' }));
        console.warn(
          '[smartOnboardingService] stakeholder submit failed:',
          result.message || result.error,
        );
      }
    } catch (error) {
      console.warn('[smartOnboardingService] stakeholder submit network error:', error);
    }
  }
}

export const smartOnboardingService = new SmartOnboardingService();
